import { Spacer, TextSubtitle } from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
} from '@pypestream/translations';
import { type FC } from 'react';
import { PuffLoader } from 'react-spinners';

import { useLiveRegionReady } from '../../hooks/use-live-region-ready';

import './loader.scss';

export const Loader: FC<{
  relative?: boolean;
  i18nKey?: string;
  progress?: string | number;
  /**
   * Whether this loader announces itself. `false` is for a caller that already
   * keeps its own live region mounted for the same state — `pages/builder.tsx`
   * does, and two regions saying the same thing is two announcements.
   */
  announce?: boolean;
}> = ({
  relative,
  i18nKey = 'pro-studio/common:loading',
  progress,
  announce = true,
}) => {
  const liveRegionReady = useLiveRegionReady();

  return (
    <div
      className={`c-ps-loader ${relative ? '' : 'u-full-width u-full-height u-overflow-hidden'}`}
      // The area this component stands in front of is busy. It is on the
      // wrapper rather than on the region it replaces because the loader is
      // rendered INSTEAD of that region, never around it.
      aria-busy="true"
      style={{
        minHeight: relative ? '100%' : 'calc(100vh - 60px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      {/* The spinner and its caption are the VISUAL half, hidden from assistive
          tech because the region below is what speaks — leaving them exposed
          reads the same sentence twice.

          Each element carries `aria-hidden` ITSELF, deliberately: a wrapper
          would be the obvious way to say it once, and it cannot be one here.
          `PuffLoader` styles itself `display: inherit`, so inside a wrapper it
          adopts the WRAPPER's display — and `display: contents`, the one value
          that leaves the flex layout untouched, makes the spinner itself
          `contents` too. It then has no box of its own (measured 0x0 against
          60x60), and its two absolutely-positioned circles fall back to the
          nearest positioned ancestor, landing in the page's top-left corner. */}
      <PuffLoader
        className="c-ps-loader__spinner"
        aria-hidden="true"
        color="#5956E3"
        size={60}
        speedMultiplier={2}
      />
      <Spacer size="small" aria-hidden="true" />
      <TextSubtitle aria-hidden="true">
        <TranslationComponent i18nKey={i18nKey as TranslationKeys}>
          Loading
        </TranslationComponent>
        {progress ? `: ${progress}%` : '...'}
      </TextSubtitle>

      {/* PSTUDIO-4241 (WCAG 2.1 AA, 4.1.3 Status Messages): the loading state
          was previously silent — no role, no live region, so nothing told a
          screen-reader user that work was in progress.

          The text is withheld for one render (`useLiveRegionReady`), which is
          the whole mechanism: this region enters the document together with
          the loader, and a region that arrives already holding its text is
          announced inconsistently. Empty first, filled on the next pass, makes
          it an ordinary content change.

          KNOWN LIMIT, stated rather than implied: this region unmounts with the
          loader, so it cannot announce that loading FINISHED. Only a caller
          whose region outlives the loading state can — `pages/builder.tsx`
          does exactly that for Workflows, and passes `announce={false}` here so
          the two do not both speak. */}
      {announce && (
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="u-visually-hidden"
        >
          {liveRegionReady ? (
            <TranslationComponent i18nKey={i18nKey as TranslationKeys}>
              Loading
            </TranslationComponent>
          ) : null}
        </span>
      )}
    </div>
  );
};
